import * as React from "react";
import {
  StyleSheet,
  ScrollView,
  TextInput,
  useAnimatedValue,
  View as DefalutView,
} from "react-native";
import useThemeColor from "../../hooks/useThemeColor";
import Colors from "../../constants/Colors";
import Text from "../Themed/Text";
import View from "../Themed/View";
import { Feather } from "@expo/vector-icons";
import { Controller } from "react-hook-form";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import Pressable from "../Pressable";

type DropdownItem = {
  value: number;
  text: string;
};

type ThemeProps = {
  lightColor?: string;
  darkColor?: string;
  transparent?: boolean;
  checked?: boolean;
  label?: string;
  control?: any;
  name: string;
  required?: boolean;
  placeholder?: string;
  errors?: any;
  setValue?: any;
  multiSelect?: boolean;
  data: Array<DropdownItem>;
};

export type DropdownProps = ThemeProps & DefalutView["props"];

export default function Dropdown(props: DropdownProps) {
  const {
    style,
    lightColor,
    darkColor,
    transparent = false,
    checked = false,
    label = "",
    control,
    name,
    required = false,
    placeholder = "",
    errors,
    setValue,
    multiSelect = false,
    data,
    ...otherProps
  } = props;

  const backgroundColor = transparent
    ? "transparent"
    : useThemeColor({ light: lightColor, dark: darkColor }, "background");

  const color = useThemeColor({ light: darkColor, dark: lightColor }, "text");

  const [selectedItems, setSelectedItems] = React.useState<Array<string>>([]);

  const textInput = React.useRef<TextInput>(null);

  const shareValue = useSharedValue(0);

  const bodyHeight = useAnimatedStyle(() => {
    return { height: interpolate(shareValue.value, [0, 1], [0, 200]) };
  });

  const iconStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          rotate: `${interpolate(shareValue.value, [0, 1], [0, 180])}deg`,
        },
      ],
    };
  });

  const toggleButton = () => {
    if (shareValue.value === 0) {
      shareValue.value = withTiming(1, {
        duration: 500,
        easing: Easing.bezier(0.4, 0.0, 0.2, 1),
      });
    } else {
      shareValue.value = withTiming(0, {
        duration: 500,
        easing: Easing.bezier(0.4, 0.0, 0.2, 1),
      });
    }
  };

  return (
    <Controller
      control={control}
      rules={{
        required: required,
      }}
      render={({ field: { onChange, onBlur, value } }) => (
        <View style={style}>
          <Text
            size={16}
            lineHeight={20}
            style={{
              alignSelf: "flex-start",
              marginBottom: 4,
            }}
            weight="SemiBold"
            lightColor={Colors.nuetral_grey_700}
          >
            {label}
            {errors[name] && (
              <Text size={12} lightColor="red">
                *This is required
              </Text>
            )}
          </Text>

          <Pressable
            style={[styles.container, { backgroundColor }]}
            onPress={() => {
              if (shareValue.value === 0) {
                //textInput.current?.focus();
              } else {
                //textInput.current?.blur();
              }
              toggleButton();
            }}
          >
            <TextInput
              ref={textInput}
              style={[styles.textBox, { color }]}
              onFocus={() => {
                shareValue.value === 0 && toggleButton();
              }}
              onBlur={() => {
                shareValue.value === 1 && toggleButton();
              }}
              onChangeText={onChange}
              value={value?.text}
              placeholder={placeholder}
            ></TextInput>
            <Animated.View
              style={[
                { height: 16, width: 16, alignSelf: "center", marginRight: 10 },
                iconStyle,
              ]}
            >
              <Feather
                name={"chevron-down"}
                size={16}
                color={useThemeColor(
                  {
                    light: Colors.nuetral_grey_700,
                    dark: Colors.base_colors_white,
                  },
                  "background"
                )}
              />
            </Animated.View>
          </Pressable>
          {multiSelect && (
            <View style={{ flexDirection: "row", flexWrap: "wrap" }}>
              {selectedItems?.map((item, index) => {
                return (
                  <View
                    key={index}
                    style={{
                      backgroundColor: Colors.nuetral_grey_400,
                      borderRadius: 100,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                      margin: 4,
                    }}
                  >
                    <Text style={{ color: Colors.nuetral_grey_700 }}>
                      {item}
                    </Text>
                  </View>
                );
              })}
            </View>
          )}

          <Animated.View
            style={[
              {
                backgroundColor: "white",
                overflow: "hidden",

                position: "absolute",
                top: 55,
                width: "100%",
              },
              bodyHeight,
            ]}
          >
            <ScrollView
              style={styles.listContainer}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled={true}
              keyboardShouldPersistTaps="always"
            >
              {data?.map((item, index) => {
                return (
                  <Pressable
                    key={index}
                    onPress={() => {
                      if (!multiSelect) {
                        onChange(item);
                        setValue(name, item);
                        textInput.current?.blur();
                        toggleButton();
                      } else {
                        selectedItems.push(item.text);
                        setValue(name, selectedItems.join(","));
                        onChange(selectedItems);
                      }
                    }}
                    style={styles.listItem}
                  >
                    <Text
                      lightColor={Colors.nuetral_grey_700}
                      darkColor={Colors.nuetral_grey_700}
                    >
                      {item.text}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>
          </Animated.View>
        </View>
      )}
      name={name}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderColor: Colors.nuetral_grey_400,
    borderWidth: 1,
    borderRadius: 4,
  },
  bodyContainer: {
    // position: 'absolute',
    //   top:0,
    // bottom: 0,
    //left: 0,
    // flex:1
    // height:100
  },
  textBox: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    flex: 1,
  },
  listContainer: {
    borderWidth: 1,
    borderColor: Colors.nuetral_grey_400,
    borderRadius: 4,
  },
  listItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
});
